import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { get } from 'lodash-es';
import { TelephoneLink } from 'client/site-modules/shared/components/inventory/telephone-link/telephone-link';
import { Delivery } from 'client/site-modules/shared/components/delivery/delivery';
import { DealerLink } from 'client/site-modules/shared/components/inventory/contact-dealer/dealer-link/dealer-link';
import { getDealershipStateCityNameUrl } from 'client/site-modules/shared/utils/dealership-link-constructor';
import { isUsed } from 'client/site-modules/shared/utils/inventory-utils/is-used';
import { isFiveStarDealer } from 'client/site-modules/shared/utils/dealer-details-utils';
import { useContactDealer } from 'site-modules/shared/components/inventory/contact-dealer/contact-dealer';
import { RetailerInfoPanel } from 'site-modules/shared/components/inventory/contact-dealer/retailer-info-panel/retailer-info-panel';

export function DealerOverview({ children = null, className = '', style = undefined }) {
  return (
    <div className={classNames('dealer-overview', className)} style={style}>
      {children}
    </div>
  );
}

DealerOverview.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  style: PropTypes.shape({}),
};

DealerOverview.FiveStar = function OverviewFiveStar() {
  const { vinData } = useContactDealer();

  if (!isFiveStarDealer(vinData)) {
    return null;
  }

  return (
    <div className="text-uppercase medium text-primary-darker">
      <i className="icon-5star me-0_25" aria-hidden />
      <span className="small">Five Star Dealer</span>
    </div>
  );
};

DealerOverview.Location = function OverviewLocation({ icon = '' }) {
  const {
    vinData,
    dealerInfo,
    visitorZipCode,
    isDealerWebsiteLinkHidden,
    isTopEmbeddedLeadFormEnabled,
    dataTrackingParent,
  } = useContactDealer();
  const { address, rooftopLogicalName } = get(vinData, 'dealerInfo', {});

  const dealerShipLink = getDealershipStateCityNameUrl({
    state: address?.stateName,
    city: address?.city,
    dealership: rooftopLogicalName,
  });

  return (
    <div className="d-flex align-items-start position-relative px-0">
      {icon && <i className={classNames(icon, 'flex-shrink-0 size-14 me-0_5')} aria-hidden />}
      <DealerLink
        vinData={vinData}
        dealerInfo={dealerInfo}
        visitorZipCode={visitorZipCode}
        isDealerWebsiteLinkHidden={isDealerWebsiteLinkHidden}
        isTopEmbeddedLeadFormEnabled={isTopEmbeddedLeadFormEnabled}
        dataTrackingParent={dataTrackingParent}
        dealerShipLink={dealerShipLink}
      />
    </div>
  );
};

DealerOverview.Location.propTypes = {
  icon: PropTypes.string,
};

DealerOverview.Phone = function OverviewPhone({ useUnderlineLink = false }) {
  const { vinData, formattedPhone, isTopEmbeddedLeadFormEnabled } = useContactDealer();

  if (!formattedPhone) return null;

  const { franchiseId, rooftopId } = get(vinData, 'dealerInfo', {});
  const dealerInfoProps = {
    dataTrackingId: 'vdp_call_dealer',
    dataDealerFranchiseId: franchiseId,
    dataDealerRooftopId: rooftopId,
  };

  const phoneConfig = useUnderlineLink
    ? {
        icon: '',
        linkClassName: 'text-decoration-underline d-inline-block',
        'data-tracking-ignore-value': true,
      }
    : {
        iconClassName: 'text-primary-darker',
        linkClassName: classNames('text-primary-darker text-decoration-none', {
          'd-inline-block': isTopEmbeddedLeadFormEnabled,
          'fw-bold d-block mt-0_5': !isTopEmbeddedLeadFormEnabled,
        }),
        telephoneLinkCTAName: 'Call Dealer',
      };

  if (!useUnderlineLink)
    return <TelephoneLink formattedPhone={formattedPhone} dealerInfo={dealerInfoProps} showNumber {...phoneConfig} />;

  return (
    <div className="d-flex align-items-start px-0">
      {useUnderlineLink && (
        <>
          <i className="icon-phone2 flex-shrink-0 me-0_5 size-16" aria-hidden />
          <span>
            Call{' '}
            <TelephoneLink formattedPhone={formattedPhone} dealerInfo={dealerInfoProps} showNumber {...phoneConfig} />
          </span>
        </>
      )}
    </div>
  );
};

DealerOverview.Phone.propTypes = {
  useUnderlineLink: PropTypes.bool,
};

DealerOverview.Delivery = function OverviewDelivery({ classes: classesFromProps = '', ...restProps }) {
  const { vinData, visitorZipCode, visitorStateCode, isMobile, displayPrice } = useContactDealer();
  const parentDealershipName = get(vinData, 'dealerInfo.displayInfo.parentDealershipName', '');
  const deliveryCost = get(vinData, 'computedDisplayInfo.deliveryOptions.deliveryCost');
  const deliveryCode = get(vinData, 'computedDisplayInfo.deliveryOptions.deliveryCode', '');
  const classes = classesFromProps || (isMobile ? 'xsmall' : 'small');

  return (
    <Delivery
      {...restProps}
      parentDealershipName={parentDealershipName}
      deliveryCode={deliveryCode}
      zipCode={visitorZipCode}
      stateCode={visitorStateCode}
      isMobile={isMobile}
      isUsedType={isUsed(vinData.type)}
      deliveryCost={deliveryCost}
      price={displayPrice}
      classes={classes}
    />
  );
};

DealerOverview.Delivery.propTypes = {
  classes: PropTypes.string,
};

DealerOverview.RetailerPanel = RetailerInfoPanel;
